/**
 * Vista /batch-edits/:batchId — dettaglio di un batch in tempo reale.
 *
 * - workflow delle chiamate API (create-batch → upload → validate-payload → run → esito)
 * - statistiche per tipo di riga (nuovo / aggiornamento / cancellazione / invariato) × esito
 * - errori di validazione/esecuzione ricondotti a chunk + riga
 * - timeline delle chiamate e chunk caricati
 * - record con il loro esito, filtrabili per tipo di riga, esito, entità e chiave
 * - batch in attesa di conferma (soglia di hold superata): motivo con i numeri e pulsante
 *   «Forza esecuzione» (PORTFOLIO_MANAGEMENT, con conferma che mostra N e soglia)
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePermissions } from '../../auth/index.js';
import { ApiError } from '../../api/client.js';
import { useBatch, useBatchRecords, useForceRunBatch, useHoldThreshold } from './hooks.js';
import {
  FINISHED_STATUSES,
  type BatchDetail,
  type BatchEntity,
  type RecordKind,
  type RecordStatus,
  type RecordsQuery,
} from './types.js';
import {
  ENTITY_LABEL,
  KIND_LABEL,
  RECORD_STATUS_LABEL,
  BatchStatusBadge,
  ProgressBar,
  RecordStatusBadge,
  fmtDateTime,
  fmtDuration,
  fmtNum,
  fmtTime,
  holdLong,
  holdShort,
  kindTotals,
} from './ui.js';
import { downloadBatchRawPayload } from './api.js';
import styles from './BatchEdits.module.css';

const PAGE_SIZE = 50;
const KINDS: RecordKind[] = ['new', 'update', 'delete', 'unchanged'];

// ── Workflow ────────────────────────────────────────────────────────────────

type StepState = 'done' | 'active' | 'error' | 'todo';
interface Step {
  name: string;
  state: StepState;
  detail: string;
}

function workflow(b: BatchDetail): Step[] {
  const finished = FINISHED_STATUSES.includes(b.status);
  const ranOrRunning = finished || b.status === 'running' || b.status === 'queued';
  const rows = b.counts.products + b.counts.customers + b.counts.deployments;
  const s = b.planSummary;
  const steps: Step[] = [
    { name: 'create-batch', state: 'done', detail: fmtDateTime(b.createdAt) },
    {
      name: 'upload',
      state: b.chunks.uploaded === 0 ? 'active' : b.status === 'uploading' ? 'active' : 'done',
      detail:
        b.chunks.uploaded === 0
          ? 'nessun chunk'
          : `${b.chunks.uploaded}${b.chunks.expected !== undefined ? `/${b.chunks.expected}` : ''} chunk · ${fmtNum(rows)} righe`,
    },
    {
      name: 'validate-payload',
      state:
        b.status === 'invalid'
          ? 'error'
          : b.status === 'validated' || (ranOrRunning && b.validatedAt)
            ? 'done'
            : 'todo',
      detail:
        b.status === 'invalid'
          ? `${b.errorCount} errori`
          : s && b.validatedAt
            ? `ok · ${s.create + s.reactivate} nuovi, ${s.update} agg., ${s.delete} canc.`
            : 'in attesa',
    },
    {
      name: 'run',
      state:
        b.status === 'queued' || b.status === 'running' || b.status === 'on-hold'
          ? 'active'
          : finished && b.startedAt
            ? 'done'
            : b.status === 'failed'
              ? 'error'
              : 'todo',
      detail:
        b.status === 'on-hold' && b.hold
          ? `in attesa di conferma · ${holdShort(b.hold)}`
          : b.status === 'queued'
            ? `in coda${b.queuePosition ? ` (posizione ${b.queuePosition})` : ''} — attende altri batch`
            : b.status === 'running'
              ? `in esecuzione · ${fmtNum(b.progress.processed)}/${fmtNum(b.progress.total)}`
              : b.startedAt
                ? `avviato ${fmtTime(b.startedAt)}`
                : 'non avviato',
    },
    {
      name: 'status / run-stats',
      state:
        b.status === 'completed'
          ? 'done'
          : b.status === 'completed-with-errors' || b.status === 'failed'
            ? 'error'
            : 'todo',
      detail: finished
        ? `${b.status === 'failed' ? 'fallito' : 'concluso'} ${b.finishedAt ? fmtTime(b.finishedAt) : ''} · ${fmtDuration(b.durationMs)}`
        : 'disponibili a fine run',
    },
  ];
  return steps;
}

const STEP_CLASS: Record<StepState, string | undefined> = {
  done: styles.stepDone,
  active: styles.stepActive,
  error: styles.stepError,
  todo: styles.stepTodo,
};

function Workflow({ batch }: { batch: BatchDetail }) {
  return (
    <ol className={styles.stepper} data-testid="batch-workflow" aria-label="Workflow chiamate API">
      {workflow(batch).map((s) => (
        <li
          key={s.name}
          className={`${styles.step} ${STEP_CLASS[s.state] ?? ''}`}
          data-state={s.state}
          data-testid={`step-${s.name}`}
        >
          <div className={styles.stepName}>{s.name}</div>
          <div className={styles.stepDetail}>{s.detail}</div>
        </li>
      ))}
    </ol>
  );
}

// ── Statistiche ─────────────────────────────────────────────────────────────

const BREAKDOWN: RecordStatus[] = [
  'done',
  'failed',
  'pending-dt',
  'not-executed',
  'pending',
  'skipped',
];

function KindCards({
  batch,
  active,
  onSelect,
}: {
  batch: BatchDetail;
  active: RecordKind | undefined;
  onSelect: (k: RecordKind | undefined) => void;
}) {
  const totals = kindTotals(batch);
  const all = KINDS.reduce((a, k) => a + totals[k], 0);
  return (
    <div className={styles.kindGrid} data-testid="kind-cards">
      <button
        type="button"
        className={`${styles.kindCard} ${active === undefined ? styles.kindCardActive : ''}`}
        onClick={() => onSelect(undefined)}
      >
        <div className={styles.kindCardTitle}>Tutti i record</div>
        <div className={styles.kindCardValue}>{fmtNum(all)}</div>
        {batch.stats && (
          <div className={styles.kindCardBreakdown}>
            {BREAKDOWN.filter((st) => batch.stats!.byStatus[st] > 0).map((st) => (
              <span key={st}>
                {RECORD_STATUS_LABEL[st]}: <strong>{fmtNum(batch.stats!.byStatus[st])}</strong>
              </span>
            ))}
          </div>
        )}
      </button>
      {KINDS.map((k) => (
        <button
          key={k}
          type="button"
          className={`${styles.kindCard} ${active === k ? styles.kindCardActive : ''}`}
          onClick={() => onSelect(active === k ? undefined : k)}
          data-testid={`kind-card-${k}`}
        >
          <div className={styles.kindCardTitle}>{KIND_LABEL[k]}</div>
          <div className={styles.kindCardValue}>{fmtNum(totals[k])}</div>
          {batch.stats && (
            <div className={styles.kindCardBreakdown}>
              {BREAKDOWN.filter((st) => batch.stats!.byKind[k][st] > 0).map((st) => (
                <span key={st}>
                  {RECORD_STATUS_LABEL[st]}: <strong>{fmtNum(batch.stats!.byKind[k][st])}</strong>
                </span>
              ))}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

function EntityTable({ batch }: { batch: BatchDetail }) {
  const rows = Object.entries(batch.stats?.byEntity ?? {});
  if (rows.length === 0) return null;
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table} data-testid="entity-table">
        <thead>
          <tr>
            <th>Entità</th>
            {KINDS.map((k) => (
              <th key={k} className={styles.num}>
                {KIND_LABEL[k]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([entity, counts]) => (
            <tr key={entity}>
              <td>{ENTITY_LABEL[entity] ?? entity}</td>
              {KINDS.map((k) => (
                <td key={k} className={styles.num}>
                  {counts ? fmtNum(counts[k]) : 0}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Record ──────────────────────────────────────────────────────────────────

function RecordsPanel({
  batch,
  kind,
  onKind,
}: {
  batch: BatchDetail;
  kind: RecordKind | undefined;
  onKind: (k: RecordKind | undefined) => void;
}) {
  const [status, setStatus] = useState<RecordStatus | ''>('');
  const [entity, setEntity] = useState<BatchEntity | ''>('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const live = !FINISHED_STATUSES.includes(batch.status);

  const query: RecordsQuery = {
    page,
    pageSize: PAGE_SIZE,
    ...(kind ? { kind } : {}),
    ...(status ? { status } : {}),
    ...(entity ? { entity } : {}),
    ...(q.trim() ? { q: q.trim() } : {}),
  };
  const { data, isLoading, isError } = useBatchRecords(batch.batchId, query, live);
  const totals = kindTotals(batch);
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const reset =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(1);
    };

  return (
    <section className={styles.panel} data-testid="records-panel">
      <h2 className={styles.panelTitle}>
        Record ed esito di esecuzione
        {live && batch.status === 'running' && <span className={styles.liveTag}>LIVE</span>}
      </h2>
      <div className={styles.toolbar}>
        <div className={styles.tabs} role="tablist" aria-label="Tipo di riga">
          <button
            type="button"
            role="tab"
            aria-selected={kind === undefined}
            className={`${styles.tab} ${kind === undefined ? styles.tabActive : ''}`}
            onClick={() => reset(onKind)(undefined)}
          >
            Tutti
          </button>
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={kind === k}
              className={`${styles.tab} ${kind === k ? styles.tabActive : ''}`}
              onClick={() => reset(onKind)(k)}
              data-testid={`kind-tab-${k}`}
            >
              {KIND_LABEL[k]}
              <span className={styles.tabCount}>{fmtNum(totals[k])}</span>
            </button>
          ))}
        </div>
        <select
          className={styles.select}
          value={status}
          onChange={(e) => reset(setStatus)(e.target.value as RecordStatus | '')}
          aria-label="Filtra per esito"
          data-testid="record-status-filter"
        >
          <option value="">Tutti gli esiti</option>
          {(Object.keys(RECORD_STATUS_LABEL) as RecordStatus[]).map((s) => (
            <option key={s} value={s}>
              {RECORD_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          className={styles.select}
          value={entity}
          onChange={(e) => reset(setEntity)(e.target.value as BatchEntity | '')}
          aria-label="Filtra per entità"
        >
          <option value="">Tutte le entità</option>
          {Object.entries(ENTITY_LABEL).map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
        <input
          className={styles.input}
          type="search"
          placeholder="Cerca chiave (es. internalId)…"
          value={q}
          onChange={(e) => reset(setQ)(e.target.value)}
          aria-label="Cerca record"
        />
      </div>

      {isLoading && <p className={styles.stateMessage}>Caricamento record…</p>}
      {isError && <p className={styles.errorMessage}>Errore nel caricamento dei record.</p>}
      {data && data.total === 0 && (
        <p className={styles.stateMessage} data-testid="records-empty">
          {batch.stats
            ? 'Nessun record corrisponde ai filtri.'
            : 'Nessun record: il batch non è ancora stato validato.'}
        </p>
      )}
      {data && data.total > 0 && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table} data-testid="records-table">
              <thead>
                <tr>
                  <th className={styles.num}>#</th>
                  <th>Entità</th>
                  <th>Chiave</th>
                  <th>Tipo</th>
                  <th>Esito</th>
                  <th>Campi modificati</th>
                  <th className={styles.num}>Chunk</th>
                  <th>Dettaglio</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.seq} data-testid={`record-row-${r.seq}`}>
                    <td className={styles.num}>{r.seq + 1}</td>
                    <td>{ENTITY_LABEL[r.entity] ?? r.entity}</td>
                    <td className={styles.mono}>{r.key}</td>
                    <td>
                      {KIND_LABEL[r.kind]}
                      {r.op === 'reactivate' && <div className={styles.muted}>riattivato</div>}
                    </td>
                    <td>
                      <RecordStatusBadge status={r.status} />
                    </td>
                    <td className={styles.mono}>{r.changedFields?.join(', ') ?? ''}</td>
                    <td className={styles.num}>{r.chunkIndex ?? '–'}</td>
                    <td className={styles.recordError}>{r.error ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.pager}>
            <span>
              {fmtNum((page - 1) * PAGE_SIZE + 1)}–{fmtNum(Math.min(page * PAGE_SIZE, data.total))}{' '}
              di {fmtNum(data.total)}
            </span>
            <button
              type="button"
              className={styles.pagerButton}
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              ‹ Prec.
            </button>
            <button
              type="button"
              className={styles.pagerButton}
              disabled={page >= pages}
              onClick={() => setPage(page + 1)}
              data-testid="records-next"
            >
              Succ. ›
            </button>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Scarica la copia RAW del payload ricevuto dall'API (i chunk tali e quali, prima della
 * normalizzazione) come un unico file JSON.
 */
function DownloadRawPayload({ batchId, chunks }: { batchId: string; chunks: number }) {
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  async function scarica() {
    setState('loading');
    try {
      await downloadBatchRawPayload(batchId);
      setState('idle');
    } catch {
      setState('error');
    }
  }
  return (
    <span className={styles.panelActions}>
      <button
        type="button"
        className={styles.btnSecondary}
        onClick={() => void scarica()}
        disabled={chunks === 0 || state === 'loading'}
        title="Body dei chunk così come ricevuti dall'API, prima della normalizzazione: un unico file JSON"
        data-testid="btn-download-raw-payload"
      >
        {state === 'loading' ? 'Download…' : 'Scarica payload RAW'}
      </button>
      {state === 'error' && (
        <span className={styles.errorInline} role="alert" data-testid="download-raw-error">
          Download non riuscito.
        </span>
      )}
    </span>
  );
}

// ── Hold / force-run ────────────────────────────────────────────────────────

/** Testo del dialogo di conferma del force-run: N, dettaglio, soglia in vigore. */
export function forceRunConfirmText(batch: BatchDetail, currentThreshold?: number): string {
  const h = batch.hold!;
  const b = h.breakdown;
  const soglia = currentThreshold ?? h.threshold;
  return (
    `Forzare l'esecuzione del batch "${batch.batchId}"?\n\n` +
    `Modifiche: ${fmtNum(h.changes)} (${fmtNum(b.create + b.reactivate)} nuovi, ` +
    `${fmtNum(b.update)} aggiornamenti, ${fmtNum(b.delete)} cancellazioni)\n` +
    `Soglia: ${fmtNum(soglia)} — senza conferma si esegue solo con meno di ${fmtNum(soglia)} modifiche.\n\n` +
    `Il payload viene rivalidato sul catalogo attuale e il batch entra nella coda di esecuzione. ` +
    `L'azione è registrata nell'audit log.`
  );
}

/**
 * Pannello del batch in attesa di conferma: motivo con i numeri e, per chi ha
 * PORTFOLIO_MANAGEMENT, il pulsante «Forza esecuzione». Dopo un force-run mostra chi l'ha fatto.
 */
function HoldPanel({ batch }: { batch: BatchDetail }) {
  const { can } = usePermissions();
  const puoForzare = can('PORTFOLIO_MANAGEMENT');
  const onHold = batch.status === 'on-hold';
  const { data: settings } = useHoldThreshold(onHold);
  const { mutate, isPending, isError, error } = useForceRunBatch(batch.batchId);
  const hold = batch.hold;
  if (!hold) return null;

  if (!onHold) {
    if (!hold.forcedBy) return null;
    return (
      <section className={`${styles.panel} ${styles.holdPanel}`} data-testid="batch-hold-forced">
        <p className={styles.holdReason}>
          Esecuzione <strong>forzata</strong> da <strong>{hold.forcedBy}</strong>
          {hold.forcedAt ? ` il ${fmtDateTime(hold.forcedAt)}` : ''}: il batch era in attesa di
          conferma con {holdShort(hold)}.
        </p>
      </section>
    );
  }

  function forza() {
    if (!window.confirm(forceRunConfirmText(batch, settings?.holdThreshold))) return;
    mutate();
  }

  return (
    <section className={`${styles.panel} ${styles.holdPanel}`} data-testid="batch-hold-panel">
      <h2 className={styles.panelTitle}>In attesa di conferma</h2>
      <p className={styles.holdReason} data-testid="batch-hold-reason">
        Il batch non è stato eseguito: {holdLong(hold)}
      </p>
      {settings && settings.holdThreshold !== hold.threshold && (
        <p className={styles.muted} data-testid="batch-hold-current-threshold">
          Soglia attuale: {fmtNum(settings.holdThreshold)} (era {fmtNum(hold.threshold)} quando il
          batch è andato in attesa). Il client può rilanciare il run: parte da solo se ora le
          modifiche sono sotto soglia.
        </p>
      )}
      <div className={styles.holdActions}>
        {puoForzare ? (
          <button
            type="button"
            className={styles.btnDanger}
            onClick={forza}
            disabled={isPending}
            data-testid="btn-force-run"
          >
            {isPending ? 'Avvio…' : 'Forza esecuzione'}
          </button>
        ) : (
          <span className={styles.muted} data-testid="force-run-denied">
            Per forzare l&apos;esecuzione serve il permesso PORTFOLIO_MANAGEMENT.
          </span>
        )}
        <span className={styles.muted}>
          In attesa dal {fmtDateTime(hold.heldAt)} · gli amministratori configurati ricevono un
          avviso via email.
        </span>
        {isError && (
          <span className={styles.errorInline} role="alert" data-testid="force-run-error">
            {error instanceof ApiError ? error.message : 'Esecuzione forzata non riuscita.'}
          </span>
        )}
      </div>
    </section>
  );
}

// ── Pagina ──────────────────────────────────────────────────────────────────

export function BatchEditDetailPage() {
  const { batchId = '' } = useParams<{ batchId: string }>();
  const { data: batch, isLoading, isError, error } = useBatch(batchId);
  const [kind, setKind] = useState<RecordKind | undefined>(undefined);

  return (
    <div>
      <Link to="/batch-edits" className={styles.backLink}>
        ‹ Tutti i batch
      </Link>
      <div className={styles.pageHeader}>
        <h1 className={`${styles.pageTitle} ${styles.titleMono}`} data-testid="batch-title">
          {batchId}
        </h1>
        {batch && <BatchStatusBadge status={batch.status} />}
        {batch && !FINISHED_STATUSES.includes(batch.status) && (
          <span className={styles.liveTag}>LIVE</span>
        )}
      </div>

      {isLoading && <p className={styles.stateMessage}>Caricamento batch…</p>}
      {isError && (
        <p className={styles.errorMessage}>
          {(error as { status?: number })?.status === 404
            ? 'Batch non trovato.'
            : 'Errore nel caricamento del batch.'}
        </p>
      )}

      {batch && (
        <>
          <section className={styles.panel}>
            {batch.description && <p className={styles.pageSubtitle}>{batch.description}</p>}
            <div className={styles.metaGrid}>
              <div>
                <span className={styles.metaLabel}>Creato da</span>
                {batch.createdBy}
              </div>
              <div>
                <span className={styles.metaLabel}>Creato</span>
                {fmtDateTime(batch.createdAt)}
              </div>
              <div>
                <span className={styles.metaLabel}>Modalità</span>
                {batch.mode}
                {batch.prune ? ' · prune' : ''}
              </div>
              <div>
                <span className={styles.metaLabel}>Righe caricate</span>
                {fmtNum(batch.counts.products)} prodotti · {fmtNum(batch.counts.customers)} clienti
                · {fmtNum(batch.counts.deployments)} forniture
              </div>
              <div>
                <span className={styles.metaLabel}>Durata esecuzione</span>
                {fmtDuration(batch.durationMs)}
              </div>
              <div>
                <span className={styles.metaLabel}>Avanzamento</span>
                <ProgressBar batch={batch} />
              </div>
            </div>
          </section>

          <HoldPanel batch={batch} />

          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>Workflow API</h2>
            <Workflow batch={batch} />
          </section>

          {batch.errors.length > 0 && (
            <section className={styles.panel} data-testid="batch-errors">
              <h2 className={styles.panelTitle}>
                Errori ({fmtNum(batch.errorCount)}
                {batch.errorCount > batch.errors.length ? `, primi ${batch.errors.length}` : ''})
              </h2>
              <ul className={styles.errorList}>
                {batch.errors.map((e, i) => (
                  <li key={i} className={styles.errorItem}>
                    <span className={styles.errorCode}>{e.code}</span>
                    {e.chunkIndex !== undefined && (
                      <span className={styles.mono}>
                        chunk {e.chunkIndex} · {e.chunkPath}{' '}
                      </span>
                    )}
                    {e.chunkIndex === undefined && e.path && (
                      <span className={styles.mono}>{e.path} </span>
                    )}
                    — {e.message}
                    {e.hint && <div className={styles.muted}>{e.hint}</div>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>Statistiche per tipo di riga</h2>
            {!batch.stats && !batch.planSummary ? (
              <p className={styles.stateMessage}>Disponibili dopo validate-payload.</p>
            ) : (
              <>
                <KindCards batch={batch} active={kind} onSelect={setKind} />
                <div style={{ height: '0.75rem' }} />
                <EntityTable batch={batch} />
              </>
            )}
          </section>

          <RecordsPanel batch={batch} kind={kind} onKind={setKind} />

          <div className={styles.twoCols}>
            <section className={styles.panel}>
              <h2 className={styles.panelTitle}>Timeline chiamate</h2>
              <ol className={styles.timeline} data-testid="batch-timeline">
                {batch.events.map((e, i) => (
                  <li key={i} className={styles.timelineItem}>
                    <span className={styles.timelineTime} title={fmtDateTime(e.at)}>
                      {fmtTime(e.at)}
                    </span>
                    <span className={styles.timelineType}>{e.type}</span>
                    <span>
                      {e.message}
                      {e.actor && <span className={styles.muted}> · {e.actor}</span>}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
            <section className={styles.panel}>
              <h2 className={styles.panelTitle}>
                Chunk caricati
                <DownloadRawPayload batchId={batch.batchId} chunks={batch.chunkList.length} />
              </h2>
              {batch.chunkList.length === 0 ? (
                <p className={styles.stateMessage}>Nessun chunk.</p>
              ) : (
                <div className={styles.tableWrap}>
                  <table className={styles.table} data-testid="chunk-table">
                    <thead>
                      <tr>
                        <th className={styles.num}>Chunk</th>
                        <th>Caricato</th>
                        <th className={styles.num}>Prodotti</th>
                        <th className={styles.num}>Clienti</th>
                        <th className={styles.num}>Forniture</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batch.chunkList.map((c) => (
                        <tr key={c.chunkIndex}>
                          <td className={styles.num}>{c.chunkIndex}</td>
                          <td>{fmtTime(c.uploadedAt)}</td>
                          <td className={styles.num}>{fmtNum(c.counts.products)}</td>
                          <td className={styles.num}>{fmtNum(c.counts.customers)}</td>
                          <td className={styles.num}>{fmtNum(c.counts.deployments)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
